import * as Prism from 'prismjs';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-tsx';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-go';
import 'prismjs/components/prism-rust';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-markdown';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-scss';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-docker';
import 'prismjs/components/prism-graphql';

// Language mapping from file extension to Prism language identifier
const extensionToLanguage: Record<string, string> = {
  // TypeScript/JavaScript
  ts: 'typescript',
  tsx: 'tsx',
  js: 'javascript',
  jsx: 'jsx',
  mjs: 'javascript',
  cjs: 'javascript',

  // Python
  py: 'python',
  pyw: 'python',

  // Go
  go: 'go',

  // Rust
  rs: 'rust',

  // Java
  java: 'java',

  // Data formats
  json: 'json',
  yaml: 'yaml',
  yml: 'yaml',

  // Markdown
  md: 'markdown',
  mdx: 'markdown',

  // Shell
  sh: 'bash',
  bash: 'bash',
  zsh: 'bash',

  // Styles
  css: 'css',
  scss: 'scss',
  sass: 'scss',

  // SQL
  sql: 'sql',

  // Docker
  dockerfile: 'docker',

  // GraphQL
  graphql: 'graphql',
  gql: 'graphql',

  // Config files
  toml: 'toml',
  ini: 'ini',
  env: 'bash',
};

/**
 * Get Prism language identifier from file path
 */
export function getLanguageFromPath(filePath: string): string {
  const fileName = filePath.toLowerCase();

  // Special cases based on filename
  if (fileName === 'dockerfile') return 'docker';
  if (fileName.endsWith('.dockerignore')) return 'bash';
  if (fileName.endsWith('.gitignore')) return 'bash';
  if (fileName.endsWith('.env')) return 'bash';
  if (fileName.endsWith('package.json')) return 'json';
  if (fileName.endsWith('tsconfig.json')) return 'json';

  // Extract extension
  const parts = fileName.split('.');
  const extension = parts.length > 1 ? parts[parts.length - 1] ?? '' : '';

  return extensionToLanguage[extension] ?? 'text';
}

/**
 * Get human-readable language name
 */
export function getLanguageName(language: string): string {
  const nameMap: Record<string, string> = {
    typescript: 'TypeScript',
    tsx: 'TypeScript React',
    javascript: 'JavaScript',
    jsx: 'JavaScript React',
    python: 'Python',
    go: 'Go',
    rust: 'Rust',
    java: 'Java',
    json: 'JSON',
    yaml: 'YAML',
    markdown: 'Markdown',
    bash: 'Shell',
    css: 'CSS',
    scss: 'SCSS',
    sql: 'SQL',
    docker: 'Docker',
    graphql: 'GraphQL',
    text: 'Text',
  };

  return nameMap[language] || language.toUpperCase();
}

/**
 * Highlight code using Prism
 */
export function highlightCode(code: string, language: string): string {
  const grammar = Prism.languages[language as keyof typeof Prism.languages];
  if (!grammar) {
    return Prism.util.encode(code) as string;
  }

  return Prism.highlight(code, grammar, language);
}

/**
 * Check if a language is supported
 */
export function isLanguageSupported(language: string): boolean {
  return language in Prism.languages;
}

/**
 * Get all supported languages
 */
export function getSupportedLanguages(): string[] {
  return Object.keys(Prism.languages);
}
